(() => {
  'use strict';

  /* =========================================
     WAMBOLT HORIZONTAL STORY
     ========================================= */

  const ROOT_SELECTOR =
    '.wambolt-horizontal-story';

  const PANEL_SELECTOR =
    '.wambolt-horizontal-story__panel';

  const TEXTURE_SELECTOR =
    '.wambolt-horizontal-story__panel-texture';

  const HORIZONTAL_QUERY =
    window.matchMedia(
      '(min-width: 768px)'
    );

  const DESKTOP_SCROLLER_QUERY =
    window.matchMedia(
      '(min-width: 990px)'
    );

  const REDUCED_MOTION_QUERY =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  const instances =
    new WeakMap();

  let dependencyTimer = null;
  let dependencyAttempts = 0;

  const MAX_DEPENDENCY_ATTEMPTS = 40;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function dependenciesReady() {
    return Boolean(
      window.gsap &&
      window.ScrollTrigger
    );
  }


  /* =========================================
     ROOT COLLECTION
     ========================================= */

  function getRoots(
    scope = document
  ) {
    const roots = [];

    if (
      scope instanceof Element &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    if (
      scope &&
      typeof scope.querySelectorAll ===
        'function'
    ) {
      roots.push(
        ...scope.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    return Array.from(
      new Set(roots)
    );
  }


  /* =========================================
     ACTIVE SCROLLER
     ========================================= */

  function getActiveScroller() {
    if (
      !DESKTOP_SCROLLER_QUERY.matches
    ) {
      return window;
    }

    if (
      window.wamboltScrollElement instanceof
      Element
    ) {
      return window.wamboltScrollElement;
    }

    return (
      document.querySelector(
        '.page-wrapper'
      ) ||
      window
    );
  }


  /* =========================================
     VIEWPORT GEOMETRY
     ========================================= */

  function getViewportWidth(
    scroller
  ) {
    if (scroller === window) {
      return window.innerWidth;
    }

    return scroller.clientWidth;
  }


  function getScrollDistance(
    root,
    scroller
  ) {
    return Math.max(
      0,
      root.scrollWidth -
        getViewportWidth(scroller)
    );
  }


  /* =========================================
     PANEL / TEXTURE COLLECTION
     ========================================= */

  function getPanels(
    root
  ) {
    return Array.from(
      root.querySelectorAll(
        PANEL_SELECTOR
      )
    );
  }


  function getTextureLayers(
    panels
  ) {
    return panels
      .map(
        (panel) =>
          panel.querySelector(
            TEXTURE_SELECTOR
          )
      )
      .filter(
        (layer) =>
          layer instanceof HTMLElement
      );
  }


  /* =========================================
     RESET
     ========================================= */

  function resetTransforms(
    panels,
    textureLayers
  ) {
    if (!window.gsap) {
      return;
    }

    if (panels.length) {
      gsap.set(
        panels,
        {
          clearProps:
            'transform'
        }
      );
    }

    if (textureLayers.length) {
      gsap.set(
        textureLayers,
        {
          clearProps:
            'transform'
        }
      );
    }
  }


  /* =========================================
     DESTROY INSTANCE
     ========================================= */

  function destroyRoot(
    root
  ) {
    const instance =
      instances.get(root);

    if (!instance) {
      const panels =
        getPanels(root);

      const textureLayers =
        getTextureLayers(
          panels
        );

      resetTransforms(
        panels,
        textureLayers
      );

      root.removeAttribute(
        'data-horizontal-story-ready'
      );

      root.removeAttribute(
        'data-horizontal-story-mode'
      );

      return;
    }


    if (instance.timeline) {
      if (
        instance.timeline.scrollTrigger
      ) {
        instance.timeline
          .scrollTrigger
          .kill();
      }

      instance.timeline.kill();
    }


    resetTransforms(
      instance.panels,
      instance.textureLayers
    );


    root.removeAttribute(
      'data-horizontal-story-ready'
    );

    root.removeAttribute(
      'data-horizontal-story-mode'
    );


    instances.delete(root);
  }


  /* =========================================
     INITIALIZE INSTANCE
     ========================================= */

  function initRoot(
    root
  ) {
    if (
      !root ||
      instances.has(root) ||
      !dependenciesReady()
    ) {
      return;
    }


    const panels =
      getPanels(root);


    if (!panels.length) {
      return;
    }


    const textureLayers =
      getTextureLayers(
        panels
      );


    const horizontal =
      HORIZONTAL_QUERY.matches &&
      !REDUCED_MOTION_QUERY.matches &&
      panels.length > 1;


    const instance = {
      panels,
      textureLayers,
      timeline: null
    };


    instances.set(
      root,
      instance
    );


    resetTransforms(
      panels,
      textureLayers
    );


    /* =========================================
       VERTICAL / REDUCED MOTION
       ========================================= */

    if (!horizontal) {
      root.setAttribute(
        'data-horizontal-story-mode',
        HORIZONTAL_QUERY.matches
          ? 'reduced-motion'
          : 'vertical'
      );

      root.setAttribute(
        'data-horizontal-story-ready',
        ''
      );

      return;
    }


    /* =========================================
       HORIZONTAL MODE
       ========================================= */

    root.setAttribute(
      'data-horizontal-story-mode',
      'horizontal'
    );


    const scroller =
      getActiveScroller();


    const distance =
      () =>
        getScrollDistance(
          root,
          scroller
        );


    const panelCount =
      panels.length;


    const travelPercent =
      100 *
      (panelCount - 1);


    /*
     * =========================================
     * TEXTURE BASE POSITIONS
     * =========================================
     *
     * Every texture layer begins aligned to
     * the LEFT EDGE of the horizontal story.
     *
     * Example with four 100vw panels:
     *
     * Panel 1 texture:    0%
     * Panel 2 texture: -100%
     * Panel 3 texture: -200%
     * Panel 4 texture: -300%
     *
     * Because each texture layer is exactly
     * one panel wide, this places every layer
     * in the exact same visual coordinate
     * system before horizontal movement begins.
     */

    textureLayers.forEach(
      (layer, index) => {
        gsap.set(
          layer,
          {
            xPercent:
              -100 * index,

            force3D:
              true
          }
        );
      }
    );


    /*
     * =========================================
     * SINGLE COMPOSITOR TIMELINE
     * =========================================
     *
     * Panels move LEFT.
     *
     * Texture layers move RIGHT by the exact
     * opposite amount.
     *
     * Both transformations belong to the same
     * GSAP timeline and therefore reach the
     * browser compositor together.
     *
     * There are intentionally:
     *
     * - no onUpdate callbacks
     * - no background-position changes
     * - no CSS-variable updates while scrolling
     */


    instance.timeline =
      gsap.timeline(
        {
          defaults: {
            duration: 1,
            ease: 'none'
          },

          scrollTrigger: {
            trigger:
              root,

            scroller,

            start:
              'top top',

            end:
              () =>
                `+=${distance()}`,

            pin:
              true,

            pinSpacing:
              true,

            scrub:
              1,

            anticipatePin:
              1,

            refreshPriority:
              10,

            invalidateOnRefresh:
              true
          }
        }
      );


    /*
     * PANELS
     *
     * Existing approved movement.
     */

    instance.timeline.to(
      panels,
      {
        xPercent:
          -travelPercent,

        force3D:
          true
      },
      0
    );


    /*
     * TEXTURES
     *
     * Exact inverse of panel movement.
     *
     * Panel index is included in both the
     * starting and ending values so every
     * texture remains visually aligned to
     * the same viewport coordinate system.
     */

    textureLayers.forEach(
      (layer, index) => {
        instance.timeline.to(
          layer,
          {
            xPercent:
              (
                -100 * index
              ) +
              travelPercent,

            force3D:
              true
          },
          0
        );
      }
    );


    root.setAttribute(
      'data-horizontal-story-ready',
      ''
    );
  }


  /* =========================================
     INITIALIZE SCOPE
     ========================================= */

  function initScope(
    scope = document
  ) {
    if (!dependenciesReady()) {
      return false;
    }

    getRoots(scope)
      .forEach(
        initRoot
      );

    return true;
  }


  /* =========================================
     DESTROY SCOPE
     ========================================= */

  function destroyScope(
    scope = document
  ) {
    getRoots(scope)
      .forEach(
        destroyRoot
      );
  }


  /* =========================================
     SCROLL SYSTEM REFRESH
     ========================================= */

  function refreshScrollSystems() {
    window.requestAnimationFrame(
      () => {
        window.requestAnimationFrame(
          () => {
            const lenis =
              window.wamboltLenis;


            if (
              lenis &&
              typeof lenis.resize ===
                'function'
            ) {
              lenis.resize();
            }


            if (
              window.ScrollTrigger &&
              typeof ScrollTrigger
                .refresh ===
                'function'
            ) {
              if (
                typeof ScrollTrigger
                  .sort ===
                  'function'
              ) {
                ScrollTrigger.sort();
              }

              ScrollTrigger.refresh();
            }
          }
        );
      }
    );
  }


  /* =========================================
     REBUILD
     ========================================= */

  function rebuildScope(
    scope = document
  ) {
    destroyScope(scope);

    window.requestAnimationFrame(
      () => {
        window.requestAnimationFrame(
          () => {
            initScope(scope);

            refreshScrollSystems();
          }
        );
      }
    );
  }


  function rebuildAll() {
    rebuildScope(
      document
    );
  }


  /* =========================================
     DEPENDENCY SAFE START
     ========================================= */

  function start() {
    if (
      initScope(document)
    ) {
      dependencyAttempts = 0;

      if (dependencyTimer) {
        window.clearTimeout(
          dependencyTimer
        );

        dependencyTimer = null;
      }

      refreshScrollSystems();

      return;
    }


    if (
      dependencyAttempts >=
      MAX_DEPENDENCY_ATTEMPTS
    ) {
      console.warn(
        'Wambolt horizontal story could not initialize because GSAP or ScrollTrigger was unavailable.'
      );

      return;
    }


    dependencyAttempts += 1;


    dependencyTimer =
      window.setTimeout(
        start,
        100
      );
  }


  /* =========================================
     BREAKPOINT CHANGES
     ========================================= */

  function handleModeChange() {
    rebuildAll();
  }


  [
    HORIZONTAL_QUERY,
    DESKTOP_SCROLLER_QUERY,
    REDUCED_MOTION_QUERY
  ].forEach(
    (query) => {
      if (
        typeof query.addEventListener ===
          'function'
      ) {
        query.addEventListener(
          'change',
          handleModeChange
        );
      } else if (
        typeof query.addListener ===
          'function'
      ) {
        query.addListener(
          handleModeChange
        );
      }
    }
  );


  /* =========================================
     LENIS SCROLLER CHANGE
     ========================================= */

  window.addEventListener(
    'wambolt:lenis:ready',
    rebuildAll
  );


  /* =========================================
     BARBA LIFECYCLE
     ========================================= */

  window.addEventListener(
    'wambolt:page:before-leave',
    (event) => {
      const container =
        event.detail?.container;

      if (container) {
        destroyScope(
          container
        );
      }
    }
  );


  window.addEventListener(
    'wambolt:page:enter',
    (event) => {
      const container =
        event.detail?.container;

      if (container) {
        rebuildScope(
          container
        );
      }
    }
  );


  /* =========================================
     SHOPIFY THEME EDITOR
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      rebuildScope(
        event.target
      );
    }
  );


  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyScope(
        event.target
      );
    }
  );


  document.addEventListener(
    'shopify:section:reorder',
    rebuildAll
  );


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (
    document.readyState ===
      'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true
      }
    );
  } else {
    start();
  }
})();